import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  Chip,
  Grid,
  Tooltip
} from '@mui/material';
import {
  Person,
  Group,
  TrendingUp,
  Assignment
} from '@mui/icons-material';
import {
  calculateProgressPercentage,
  calculateWorkloadLevel,
  getProgressColor
} from '../../utils/progressCalculations';

interface TeamMember {
  assignee: string;
  total: number;
  completed: number;
  inProgress: number;
  completionRate: number;
  team?: string;
  epicKey?: string;
  epicTeam?: string;
}

interface TeamWorkloadChartProps {
  workloadData: TeamMember[];
  epics?: any[]; // Add epics data to get team information
  title?: string;
}

interface TeamGroup {
  teamName: string;
  members: TeamMember[];
  totalPoints: number;
  completedPoints: number;
  teamCompletionRate: number;
}

const TeamWorkloadChart: React.FC<TeamWorkloadChartProps> = ({
  workloadData,
  epics = [],
  title = "Epic Team Member Workload"
}) => {
  // Import the existing team mapping service
  const getTeamFromSprint = (sprintName: string): string => {
    if (!sprintName || typeof sprintName !== 'string') {
      return 'Unknown Team';
    }

    const sprintLower = sprintName.toLowerCase().trim();

    // Use fallback team names for this component (since it needs to be synchronous)
    // TODO: Consider making this component async to use the full team configuration API
    const FALLBACK_TEAM_NAMES = [
      'AI Services', 'AI/Search', 'Cloud IdM', 'CRDB', 'DBM', 'DevX_Infra',
      'InfraDevops Cloud', 'PC', 'RedisCore', 'RedisInsight', 'SM Core', 'ZTC'
    ];

    // Check for exact team name matches (case insensitive)
    for (const teamName of FALLBACK_TEAM_NAMES) {
      const teamLower = teamName.toLowerCase();
      if (sprintLower.includes(teamLower)) {
        return teamName;
      }
    }

    // Try to extract from common patterns like "TeamName - Something"
    const dashMatch = sprintName.match(/^([^-]+)\s*-/);
    if (dashMatch) {
      const extractedName = dashMatch[1].trim();

      // Check if extracted name matches any team (case insensitive)
      for (const teamName of FALLBACK_TEAM_NAMES) {
        if (teamName.toLowerCase() === extractedName.toLowerCase()) {
          return teamName;
        }
      }

      // Return extracted name if it looks reasonable
      if (extractedName.length > 1) {
        return extractedName;
      }
    }

    // Last resort: return the original sprint name or Unknown
    return sprintName.length > 0 ? sprintName : 'Unknown Team';
  };

  // Create a mapping of assignees to their epic teams
  const createAssigneeToTeamMapping = (): Map<string, string> => {
    const assigneeTeamMap = new Map<string, string>();
    const assigneeTeamWorkload = new Map<string, Map<string, number>>();

    epics.forEach(epic => {
      const teamSprint = epic.customFields?.teamSprint || epic.team || '';
      const epicTeam = getTeamFromSprint(teamSprint);

      // Get workload data for this epic
      const epicWorkload = epic.insights?.team?.workloadDistribution || [];
      epicWorkload.forEach((member: any) => {
        const assignee = member.assignee;

        // Track workload per team for each assignee
        if (!assigneeTeamWorkload.has(assignee)) {
          assigneeTeamWorkload.set(assignee, new Map<string, number>());
        }

        const assigneeTeams = assigneeTeamWorkload.get(assignee)!;
        const currentWorkload = assigneeTeams.get(epicTeam) || 0;
        assigneeTeams.set(epicTeam, currentWorkload + member.total);
      });
    });

    // Assign each assignee to the team where they have the most work
    assigneeTeamWorkload.forEach((teamWorkloads, assignee) => {
      let maxWorkload = -1; // Start with -1 to handle zero workload cases
      let primaryTeam = 'Unknown Team';

      // First pass: find the team with maximum workload
      teamWorkloads.forEach((workload, team) => {
        if (workload > maxWorkload) {
          maxWorkload = workload;
          primaryTeam = team;
        }
      });

      // Second pass: prefer non-unknown teams when there are multiple teams
      // This handles cases where workload is equal (including zero) or when we want to prioritize known teams
      if (teamWorkloads.size > 1) {
        const nonUnknownTeams = Array.from(teamWorkloads.entries())
          .filter(([team]) => team !== 'Unknown Team' && team !== 'No Team/Sprint' && team !== 'No Team')
          .sort((a, b) => b[1] - a[1]); // Sort by workload descending

        if (nonUnknownTeams.length > 0) {
          // If we have known teams, prefer them over unknown teams
          // even if the workload is the same (including zero)
          const maxKnownWorkload = nonUnknownTeams[0][1];
          if (maxKnownWorkload >= maxWorkload) {
            primaryTeam = nonUnknownTeams[0][0];
          }
        }
      }

      assigneeTeamMap.set(assignee, primaryTeam);
    });

    return assigneeTeamMap;
  };

  const assigneeTeamMap = createAssigneeToTeamMapping();

  // Debug logging
  console.log('🔍 TeamWorkloadChart Debug:');
  console.log('  📊 Workload data:', workloadData);
  console.log('  🏢 Epics data:', epics);
  console.log('  🗺️ Assignee to team mapping:', Object.fromEntries(assigneeTeamMap));

  // Enhanced debug logging for team assignment
  console.log('🎯 Team Assignment Analysis:');
  epics.forEach(epic => {
    const teamSprint = epic.customFields?.teamSprint || epic.team || '';
    const epicTeam = getTeamFromSprint(teamSprint);
    const workload = epic.insights?.team?.workloadDistribution || [];
    console.log(`  📋 Epic ${epic.key}: "${teamSprint}" → "${epicTeam}"`);
    workload.forEach((member: any) => {
      const assignedTeam = assigneeTeamMap.get(member.assignee) || 'Unknown';
      console.log(`    👤 ${member.assignee}: ${member.total} points → Team: "${assignedTeam}"`);
    });
  });

  // Log each epic's team information
  epics.forEach(epic => {
    const teamSprint = epic.customFields?.teamSprint || epic.team || '';
    const epicTeam = getTeamFromSprint(teamSprint);
    console.log(`  📋 Epic ${epic.key}: teamSprint="${teamSprint}" -> team="${epicTeam}"`);
  });

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };



  // Group members by teams using the epic-based team mapping
  const groupMembersByTeam = (): TeamGroup[] => {
    const teamGroups = new Map<string, TeamMember[]>();

    // Add team mapping to each member and group them
    workloadData.forEach(member => {
      // Get team from the assignee-to-team mapping we created
      const team = assigneeTeamMap.get(member.assignee) || 'Unknown Team';
      const memberWithTeam = { ...member, team };

      if (!teamGroups.has(team)) {
        teamGroups.set(team, []);
      }
      teamGroups.get(team)!.push(memberWithTeam);
    });

    // Convert to TeamGroup objects with aggregated stats
    return Array.from(teamGroups.entries()).map(([teamName, members]) => {
      const totalPoints = members.reduce((sum, member) => sum + member.total, 0);
      const completedPoints = members.reduce((sum, member) => sum + member.completed, 0);
      const teamCompletionRate = calculateProgressPercentage(completedPoints, totalPoints);

      return {
        teamName,
        members: members.sort((a, b) => b.total - a.total), // Sort by total points descending
        totalPoints,
        completedPoints,
        teamCompletionRate
      };
    }).sort((a, b) => b.totalPoints - a.totalPoints); // Sort teams by total points descending
  };

  const teamGroups = groupMembersByTeam();
  const totalStoryPoints = workloadData.reduce((sum, member) => sum + member.total, 0);
  const totalCompleted = workloadData.reduce((sum, member) => sum + member.completed, 0);
  const overallCompletionRate = calculateProgressPercentage(totalCompleted, totalStoryPoints);

  return (
    <Card sx={{
      mb: 2, // Reduced margin
      boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
      border: '1px solid rgba(0,0,0,0.06)',
      borderRadius: 3
    }}>
      <CardContent sx={{ p: 2.5 }}> {/* Reduced padding */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}> {/* Reduced margin */}
          <Group sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} /> {/* Smaller icon and margin */}
          <Typography variant="h6" sx={{
            fontWeight: 500,
            color: 'text.primary',
            fontSize: '1.1rem', // Slightly smaller
            letterSpacing: '-0.01em'
          }}>
            {title}
          </Typography>
        </Box>

        {/* Summary Stats */}
        <Grid container spacing={1.5} sx={{ mb: 2 }}> {/* Further reduced spacing and margin */}
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', py: 1 }}> {/* Added minimal padding */}
              <Typography variant="h4" sx={{
                fontWeight: 600,
                color: 'primary.main',
                fontSize: '1.4rem', // Smaller font
                mb: 0.125, // Further reduced margin
                letterSpacing: '-0.02em',
                lineHeight: 1.2 // Tighter line height
              }}>
                {workloadData.length}
              </Typography>
              <Typography variant="body2" sx={{
                color: 'text.secondary',
                fontSize: '0.7rem', // Smaller font
                fontWeight: 400,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Team Members
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', py: 1 }}> {/* Added minimal padding */}
              <Typography variant="h4" sx={{
                fontWeight: 600,
                color: 'text.primary',
                fontSize: '1.4rem', // Smaller font
                mb: 0.125, // Further reduced margin
                letterSpacing: '-0.02em',
                lineHeight: 1.2 // Tighter line height
              }}>
                {totalStoryPoints}
              </Typography>
              <Typography variant="body2" sx={{
                color: 'text.secondary',
                fontSize: '0.7rem', // Smaller font
                fontWeight: 400,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Total Story Points
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', py: 1 }}> {/* Added minimal padding */}
              <Typography variant="h4" sx={{
                fontWeight: 600,
                color: 'success.main',
                fontSize: '1.4rem', // Smaller font
                mb: 0.125, // Further reduced margin
                letterSpacing: '-0.02em',
                lineHeight: 1.2 // Tighter line height
              }}>
                {totalCompleted}
              </Typography>
              <Typography variant="body2" sx={{
                color: 'text.secondary',
                fontSize: '0.7rem', // Smaller font
                fontWeight: 400,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Completed Points
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Box sx={{ textAlign: 'center', py: 1 }}> {/* Added minimal padding */}
              <Typography variant="h4" sx={{
                fontWeight: 600,
                color: 'info.main',
                fontSize: '1.4rem', // Smaller font
                mb: 0.125, // Further reduced margin
                letterSpacing: '-0.02em',
                lineHeight: 1.2 // Tighter line height
              }}>
                {Math.round(overallCompletionRate)}%
              </Typography>
              <Typography variant="body2" sx={{
                color: 'text.secondary',
                fontSize: '0.7rem', // Smaller font
                fontWeight: 400,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Team Completion
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Teams and Members */}
        {teamGroups.map((teamGroup, teamIndex) => (
          <Box key={teamGroup.teamName} sx={{ mb: 3 }}> {/* Reduced margin */}
            {/* Team Header */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 1.5, // Further reduced margin
              p: 2, // Further reduced padding
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : '#f1f5f9',
              color: (theme) => theme.palette.mode === 'dark' ? 'grey.100' : '#334155',
              borderRadius: 2,
              border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              minHeight: 56 // Set consistent height
            }}>
              <Group sx={{ mr: 1, fontSize: 18 }} /> {/* Smaller icon and margin */}
              <Typography variant="h6" sx={{
                fontWeight: 600,
                flex: 1,
                fontSize: '1rem', // Smaller font
                letterSpacing: '-0.01em'
              }}>
                {teamGroup.teamName}
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}> {/* Reduced gap */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{
                    opacity: 0.85,
                    fontSize: '0.7rem', // Smaller font
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 0.25 // Reduced margin
                  }}>
                    Members
                  </Typography>
                  <Typography variant="h6" sx={{
                    fontWeight: 600,
                    fontSize: '1rem' // Smaller font
                  }}>
                    {teamGroup.members.length}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{
                    opacity: 0.85,
                    fontSize: '0.7rem', // Smaller font
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 0.25 // Reduced margin
                  }}>
                    Total Points
                  </Typography>
                  <Typography variant="h6" sx={{
                    fontWeight: 600,
                    fontSize: '1rem' // Smaller font
                  }}>
                    {teamGroup.totalPoints}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" sx={{
                    opacity: 0.85,
                    fontSize: '0.7rem', // Smaller font
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 0.25 // Reduced margin
                  }}>
                    Completion
                  </Typography>
                  <Typography variant="h6" sx={{
                    fontWeight: 600,
                    fontSize: '1rem' // Smaller font
                  }}>
                    {Math.round(teamGroup.teamCompletionRate)}%
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Team Members */}
            <Grid container spacing={2}> {/* Reduced spacing */}
              {teamGroup.members.map((member, memberIndex) => {
                const workloadData = calculateWorkloadLevel(member.total, teamGroup.totalPoints, teamGroup.members.length);
                const workloadPercentage = workloadData.percentage.toFixed(1);

                return (
                  <Grid item xs={12} sm={6} md={3} key={member.assignee}> {/* Changed md={4} to md={3} for 4 per row */}
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        borderLeft: `3px solid`,
                        borderLeftColor: workloadData.color === 'error' ? 'error.main' :
                                       workloadData.color === 'warning' ? 'warning.main' :
                                       workloadData.color === 'success' ? 'success.main' :
                                       (theme) => theme.palette.mode === 'dark' ? 'grey.600' : 'grey.300',
                        border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
                        borderRadius: 2,
                        boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 1px 4px rgba(0,0,0,0.3)' : '0 1px 4px rgba(0,0,0,0.04)',
                        '&:hover': {
                          boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.1)',
                          transform: 'translateY(-1px)',
                          transition: 'all 0.2s ease-in-out'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2 }}> {/* Reduced padding */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}> {/* Reduced margin */}
                          <Avatar
                            sx={{
                              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.600' : 'primary.main',
                              width: 32, // Smaller avatar
                              height: 32, // Smaller avatar
                              mr: 1, // Reduced margin
                              fontSize: '0.8rem', // Smaller font
                              fontWeight: 500
                            }}
                          >
                            {member.assignee === 'Unassigned' ? (
                              <Person />
                            ) : (
                              getInitials(member.assignee)
                            )}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" sx={{
                              fontWeight: 500,
                              fontSize: '0.85rem', // Smaller font
                              color: 'text.primary',
                              mb: 0.25 // Reduced margin
                            }}>
                              {member.assignee}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.75 }}> {/* Reduced gap */}
                              <Chip
                                label={`${workloadData.level} (${workloadPercentage}%)`}
                                size="small"
                                color={workloadData.color as any}
                                variant="outlined"
                                sx={{
                                  fontSize: '0.7rem',
                                  height: 20,
                                  '& .MuiChip-label': {
                                    px: 1
                                  }
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>

                        {/* Progress Bar */}
                        <Box sx={{ mb: 2.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="caption" sx={{
                              color: 'text.secondary',
                              fontSize: '0.7rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              fontWeight: 500
                            }}>
                              Progress
                            </Typography>
                            <Typography variant="caption" sx={{
                              color: 'text.secondary',
                              fontSize: '0.7rem',
                              fontWeight: 500
                            }}>
                              {Math.round(member.completionRate)}%
                            </Typography>
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={member.completionRate}
                            color={getProgressColor(member.completionRate) as any}
                            sx={{
                              height: 4,
                              borderRadius: 2,
                              backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.06)'
                            }}
                          />
                        </Box>

                        {/* Story Points Breakdown */}
                        <Grid container spacing={1.5}>
                          <Grid item xs={4}>
                            <Tooltip title="Total Story Points">
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{
                                  color: 'primary.main',
                                  fontSize: '1.1rem',
                                  fontWeight: 600,
                                  mb: 0.25
                                }}>
                                  {member.total}
                                </Typography>
                                <Typography variant="caption" sx={{
                                  color: 'text.secondary',
                                  fontSize: '0.7rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  Total
                                </Typography>
                              </Box>
                            </Tooltip>
                          </Grid>
                          <Grid item xs={4}>
                            <Tooltip title="Completed Story Points">
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{
                                  color: 'success.main',
                                  fontSize: '1.1rem',
                                  fontWeight: 600,
                                  mb: 0.25
                                }}>
                                  {member.completed}
                                </Typography>
                                <Typography variant="caption" sx={{
                                  color: 'text.secondary',
                                  fontSize: '0.7rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  Done
                                </Typography>
                              </Box>
                            </Tooltip>
                          </Grid>
                          <Grid item xs={4}>
                            <Tooltip title="In Progress Story Points">
                              <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{
                                  color: 'warning.main',
                                  fontSize: '1.1rem',
                                  fontWeight: 600,
                                  mb: 0.25
                                }}>
                                  {member.inProgress}
                                </Typography>
                                <Typography variant="caption" sx={{
                                  color: 'text.secondary',
                                  fontSize: '0.7rem',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  Active
                                </Typography>
                              </Box>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}

        {teamGroups.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Assignment sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              No workload data available
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamWorkloadChart;
